import React, { FC } from 'react';

import { Button } from 'antd';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import i18n from '@/content';
import { CollapsibleWidget } from '../../Common/OverflowWidget';
import { bytesToText } from '../../utils';
import { useStores } from '../../hooks';
import {
    FolderToDisplay,
    handleUploadedFileRemovingResult,
    UploadedFile,
    UploadedItem,
} from '../../../stores';
import UploadRow from './UploadRow';
import styles from './MyFilesUploadPopup.module.scss';

const FULL_PERCENTS = 100;
const nameSpace = 'uploadFiles.myFilesPopup';

type PopupItem = UploadedFile | FolderToDisplay;

const MyFilesUploadPopup: FC = observer(() => {
    const { t } = useTranslation();
    const { appStore, filesListStore, uploadFilesStore } = useStores();

    const {
        modifiedFoldedFiles,
        uploadedFilesToJS,
        isUploading,
        removeUploadingItem,
        retry,
        retryFailedUploads,
        cancelActiveUploads,
        successfullyUploadedItems,
        clear,
    } = uploadFilesStore;

    const items = modifiedFoldedFiles as PopupItem[];
    const itemsCount = items.length;
    const hasError = uploadedFilesToJS.some(({ status }) => status === 'error');

    const totalBytes = uploadedFilesToJS.reduce((acc, { size }) => acc + (size || 0), 0);
    const uploadedBytes = uploadedFilesToJS.reduce((acc, { size, percent, status }) => {
        if (status === 'done') {
            return acc + (size || 0);
        }
        return acc + Math.floor(((size || 0) * (percent || 0)) / FULL_PERCENTS);
    }, 0);

    const getFid = (item: PopupItem): string | undefined => {
        const directFid = (item as UploadedFile).fid;
        if (directFid) {
            return directFid;
        }
        return successfullyUploadedItems.find(({ uid }) => uid === item.uid)?.fid;
    };

    const closePopup = (): void => {
        filesListStore.setCurrentSelectedFiles([]);
        clear();
        appStore.closeMyFilesUploadPopup();
    };

    const onCancel = async (item: PopupItem): Promise<void> => {
        await removeUploadingItem(item as UploadedItem);
        if (!uploadFilesStore.modifiedFoldedFiles.length) {
            appStore.closeMyFilesUploadPopup();
        }
    };

    const onDelete = async (item: PopupItem): Promise<void> => {
        const fid = getFid(item);
        const result = await removeUploadingItem(item as UploadedItem);
        const itemName = (item as FolderToDisplay).name || (item as UploadedFile).filename;
        handleUploadedFileRemovingResult(i18n.t.bind(i18n), itemName, result);
        if (result === 'success' && fid) {
            filesListStore.deleteFilesFromCurrentFolder(fid, filesListStore.displayFilesType);
        }
        if (!uploadFilesStore.modifiedFoldedFiles.length) {
            appStore.closeMyFilesUploadPopup();
        }
    };

    const onRetry = (item: PopupItem): void => {
        if (item.isFolder) {
            (item as FolderToDisplay).foldedFiles
                ?.filter(({ status }) => status === 'error')
                .forEach(({ uid }) => retry(uid));
            return;
        }
        retry(item.uid);
    };

    const onCancelAll = async (): Promise<void> => {
        await cancelActiveUploads();
        if (!uploadFilesStore.modifiedFoldedFiles.length) {
            appStore.closeMyFilesUploadPopup();
        }
    };

    const title = isUploading
        ? t(`${nameSpace}.title`, { count: itemsCount })
        : t(`${nameSpace}.doneTitle`, { count: itemsCount });

    const progressText = t(`${nameSpace}.progress`, {
        uploaded: bytesToText(uploadedBytes),
        total: bytesToText(totalBytes),
    });

    const renderRow = (item: PopupItem): JSX.Element => {
        if (item.isFolder) {
            const folder = item as FolderToDisplay;
            const count = folder.foldedFiles?.length || 0;
            return (
                <UploadRow
                    key={folder.uid}
                    name={t(`${nameSpace}.folderName`, { name: folder.name, count })}
                    size={0}
                    status={folder.status}
                    subText=""
                    onCancel={() => onCancel(folder)}
                    onDelete={() => onDelete(folder)}
                    onRetry={() => onRetry(folder)}
                />
            );
        }
        const file = item as UploadedFile;
        return (
            <UploadRow
                key={file.uid}
                name={file.name || file.filename}
                size={file.size}
                percent={file.percent}
                status={file.status}
                onCancel={() => onCancel(file)}
                onDelete={() => onDelete(file)}
                onRetry={() => onRetry(file)}
            />
        );
    };

    return (
        <CollapsibleWidget
            title={title}
            closeWidget={isUploading ? undefined : closePopup}
            renderChildren={() => (
                <>
                    <div className={styles['sub-header']}>
                        <span className={styles['progress-text']}>{progressText}</span>
                        <div className={styles.actions}>
                            {hasError && (
                                <Button
                                    type="link"
                                    className={styles['action-link']}
                                    onClick={retryFailedUploads}
                                    data-testid="myFilesUploadPopup_retryAll"
                                >
                                    {t(`${nameSpace}.retryAll`)}
                                </Button>
                            )}
                            {(isUploading || hasError) && (
                                <Button
                                    type="link"
                                    danger
                                    className={styles['action-link']}
                                    onClick={onCancelAll}
                                    data-testid="myFilesUploadPopup_cancelAll"
                                >
                                    {hasError ? t(`${nameSpace}.cancelAll`) : t(`${nameSpace}.cancel`)}
                                </Button>
                            )}
                        </div>
                    </div>
                    <div className={styles.list}>
                        {items.map(renderRow)}
                    </div>
                </>
            )}
            renderChildrenInCollapsedMode={() => (
                <div className={styles.collapsed}>
                    {isUploading ? progressText : t(`${nameSpace}.collapsedDone`, { count: itemsCount })}
                </div>
            )}
        />
    );
});

export default MyFilesUploadPopup;
